import React, { useEffect, useRef, useState } from "react";
import MuxUploader from "@mux/mux-uploader-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Check, RefreshCw, Upload, X } from "lucide-react";

export function formatUploadSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "Unknown size";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}

export default function PartCutUploadControl({ videoId, kind, asset, onRefresh }) {
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState(asset.upload_state || "idle");
  const [progress, setProgress] = useState(0);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [filename, setFilename] = useState(asset.upload_filename || "");
  const [size, setSize] = useState(asset.upload_size_bytes || 0);
  const [error, setError] = useState(asset.upload_error || "");
  const [attempt, setAttempt] = useState(0);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    if (asset.upload_state) setPhase(asset.upload_state);
    if (asset.upload_filename) setFilename(asset.upload_filename);
    if (asset.upload_size_bytes) setSize(asset.upload_size_bytes);
    if (asset.upload_error) setError(asset.upload_error);
  }, [asset.upload_state, asset.upload_filename, asset.upload_size_bytes, asset.upload_error]);

  useEffect(() => {
    if (phase !== "processing") return undefined;
    let stopped = false;
    let attempts = 0;
    let timeout;
    const poll = async () => {
      try {
        const { data } = await api.get(`/api/videos/${videoId}/parts-cuts`);
        const collection = kind === "part" ? data.parts : data.cuts;
        const current = collection.find((item) => item.id === asset.id);
        if (stopped || !current) return;
        onRefreshRef.current?.();
        if (current.upload_state === "ready") {
          setPhase("ready");
          setUploadInfo(null);
          toast.success(`${kind === "part" ? "Part" : "Cut"} upload ready`);
          return;
        }
        if (current.upload_state === "failed") {
          setPhase("failed");
          setError(current.upload_error || "Mux processing failed");
          return;
        }
      } catch {
        if (stopped) return;
      }
      attempts += 1;
      if (attempts >= 120) {
        setPhase("failed");
        setError("Processing is taking longer than expected. Retry status check or reload this video.");
        return;
      }
      timeout = setTimeout(poll, 5000);
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(timeout);
    };
  }, [phase, videoId, kind, asset.id]);

  const endpoint = async (file) => {
    if (!file) throw new Error("Choose a video file first");
    if (file.type && !file.type.startsWith("video/")) {
      setPhase("failed");
      setError("Choose a video file");
      throw new Error("Choose a video file");
    }
    if (!file.size || file.size > 100 * 1024 ** 3) {
      setPhase("failed");
      setError("File must be smaller than 100 GB");
      throw new Error("File must be smaller than 100 GB");
    }
    setFilename(file.name);
    setSize(file.size);
    setError("");
    setProgress(0);
    setPhase("uploading");
    const url = kind === "part"
      ? `/api/videos/${videoId}/parts/${asset.id}/upload`
      : `/api/videos/${videoId}/cuts/${asset.id}/upload`;
    try {
      const { data } = await api.post(url, {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        file_size_bytes: file.size,
      });
      setUploadInfo(data);
      onRefreshRef.current?.();
      return data.upload_url;
    } catch (requestError) {
      const message = requestError.response?.data?.detail || "Could not start Mux upload";
      setError(message);
      setPhase("failed");
      throw new Error(message);
    }
  };

  const cancel = async () => {
    if (!uploadInfo?.upload_id) {
      setVisible(false);
      setPhase(asset.upload_state || "idle");
      return;
    }
    setVisible(false);
    setUploadInfo(null);
    setPhase("canceled");
    setError("");
    const path = kind === "part"
      ? `/api/videos/${videoId}/parts/${asset.id}/upload/cancel`
      : `/api/videos/${videoId}/cuts/${asset.id}/upload/cancel`;
    try {
      await api.post(path);
      toast.message("Upload canceled; the previous asset is unchanged");
      onRefreshRef.current?.();
    } catch (cancelError) {
      setError(cancelError.response?.data?.detail || "Could not cancel upload");
      setPhase("failed");
    }
  };

  const retry = async () => {
    if (uploadInfo?.upload_id) {
      const path = kind === "part"
        ? `/api/videos/${videoId}/parts/${asset.id}/upload/fail`
        : `/api/videos/${videoId}/cuts/${asset.id}/upload/fail`;
      try { await api.post(path, { error: error || "Upload failed" }); } catch { /* A webhook may already have cleared the pending upload. */ }
      onRefreshRef.current?.();
    }
    setError("");
    setUploadInfo(null);
    setProgress(0);
    setPhase("idle");
    setAttempt((value) => value + 1);
    setVisible(true);
  };

  const active = phase === "uploading" || phase === "processing";
  return (
    <div className="min-w-0" data-testid={`part-cut-upload-${kind}-${asset.id}`}>
      {!visible && !active && (
        <button type="button" onClick={() => setVisible(true)}
          data-testid={`part-cut-upload-open-${kind}-${asset.id}`}
          className="inline-flex items-center gap-1 rounded border border-[#14B8A6]/40 bg-[#14B8A6]/10 px-2 py-1 text-[10px] font-semibold text-teal-100 hover:border-[#14B8A6] focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
          <Upload size={12} /> {asset.playback_id ? "Replace video" : "Upload video"}
        </button>
      )}
      {visible && phase !== "processing" && (
        <div className="mt-2 space-y-2 rounded-lg border border-white/10 bg-black/30 p-2.5" data-testid={`part-cut-upload-control-${kind}-${asset.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-300">{asset.playback_id ? "Replace media" : "Upload media"}</div>
            <button type="button" onClick={cancel} data-testid={`part-cut-upload-cancel-${kind}-${asset.id}`} className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white" title="Cancel upload"><X size={13} /></button>
          </div>
          {filename && <div className="truncate text-[10px] text-zinc-400" title={filename}>{filename} · {formatUploadSize(size)}</div>}
          <MuxUploader
            key={`${asset.id}-${attempt}`}
            endpoint={endpoint}
            chunkSize={5120}
            dynamicChunkSize
            maxFileSize={100 * 1024 ** 3}
            onUploadStart={(event) => {
              const file = event.detail?.file;
              if (file) { setFilename(file.name); setSize(file.size); }
              setPhase("uploading");
            }}
            onProgress={(event) => {
              const value = Number(event.detail || 0);
              setProgress(Math.max(0, Math.min(100, value <= 1 ? value * 100 : value)));
            }}
            onSuccess={() => {
              setProgress(100);
              setPhase("processing");
              setVisible(false);
              toast.message("Upload complete; Mux is processing the video");
              onRefresh?.();
            }}
            onUploadError={(event) => {
              const message = event.detail?.message || "Upload failed. Check your connection and retry.";
              setError(message);
              setPhase("failed");
              const path = kind === "part"
                ? `/api/videos/${videoId}/parts/${asset.id}/upload/fail`
                : `/api/videos/${videoId}/cuts/${asset.id}/upload/fail`;
              api.post(path, { error: message }).then(() => onRefreshRef.current?.()).catch(() => {});
            }}
            style={{ "--button-background-color": "var(--gio-primary)", "--button-color": "#000", "--progress-bar-fill-color": "var(--gio-primary)" }}
          />
          {active && (
            <div className="space-y-1" aria-live="polite">
              <div className="flex justify-between text-[10px] text-zinc-400"><span>{phase === "processing" ? "Processing on Mux" : "Uploading"}</span><span>{Math.round(progress)}%</span></div>
              <div className="h-1.5 overflow-hidden rounded bg-zinc-800"><div className="h-full bg-teal-400 transition-[width]" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
        </div>
      )}
      {phase === "processing" && (
        <div className="mt-1 flex items-center gap-2 text-[10px] text-amber-200" role="status" data-testid={`part-cut-upload-processing-${kind}-${asset.id}`}>
          <span className="h-2 w-2 animate-pulse rounded-full bg-amber-300" /> {filename || "Video"} · {formatUploadSize(size)} · processing on Mux
        </div>
      )}
      {phase === "ready" && (
        <div className="mt-1 flex items-center gap-1 text-[10px] text-emerald-300" role="status" data-testid={`part-cut-upload-ready-${kind}-${asset.id}`}>
          <Check size={12} /> Playback ready{asset.upload_completed_at ? ` · ${asset.upload_completed_at.slice(0, 10)}` : ""}
        </div>
      )}
      {phase === "failed" && (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-red-300" role="alert" data-testid={`part-cut-upload-error-${kind}-${asset.id}`}>
          <AlertTriangle size={12} /> <span>{error || "Upload failed"}</span>
          <button type="button" onClick={retry} className="inline-flex items-center gap-1 underline hover:text-white" data-testid={`part-cut-upload-retry-${kind}-${asset.id}`}><RefreshCw size={11} /> Retry</button>
        </div>
      )}
      {phase === "canceled" && <span className="mt-1 block text-[10px] text-zinc-500" role="status">Upload canceled; existing playback preserved.</span>}
    </div>
  );
}
